// Tab "Wartung" (W1-W15 of APP-PARITY.md), after the UX line of 19.10.2026: the main page answers "what is due, how do
// I start it?" (flush, backflush and cleaning armed remotely and run with S3, descaling, drip tray: each its state, a
// soft bar and its button, primary only when it is due), the counters and the row "Erinnerungen ›". Only the care of
// the machine: the device (firmware, restart, export, resets) is in Einstellungen → System (Dominik 23.10.2026).
// Decisions in core/screens/care.ts.
import { useId, useState, type ReactNode } from 'react';
import {
  backflushView,
  controlView,
  counterFacts,
  descaleView,
  dripView,
  dueItems,
  flushView,
  type CarePage,
} from '../../core/index.js';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  FactRow,
  fitLineOf,
  Icon,
  NavList,
  Sheet,
} from '../components/index.js';
import { useMachine } from '../machine-context.js';
import { ActionButton, Bar, ControlRow, NoteRow, OwnerNote, Row, SubScreen, TabScreen } from './parts.js';

export function CareTab({
  title,
  accountButton,
  page = { page: 'main' },
  onPage = () => undefined,
  onBack = () => undefined,
}: {
  title: string;
  accountButton?: ReactNode;
  /** the sub-page shown (#wartung/entkalken, #wartung/einstellungen), the main page by default */
  page?: CarePage;
  onPage?: (p: CarePage) => void;
  onBack?: () => void;
}) {
  if (page.page === 'descale') return <DescalePage onBack={onBack} onPage={onPage} />;
  if (page.page === 'settings') return <CareSettingsPage onBack={onBack} />;
  // prototype "Wartung": what is due first, then the programmes with a play button each, the counters, the reminders
  return (
    <TabScreen tab="wartung" title={title} accountButton={accountButton}>
      <DueCard />
      <ProgramsCard onPage={onPage} />
      <CountsCard />
      <RemindersRow onPage={onPage} />
    </TabScreen>
  );
}

/** The row one level deeper (prototype "Wartung": "Erinnerungen ›"): the reminders and the backflush's settings. */
function RemindersRow({ onPage }: { onPage: (p: CarePage) => void }) {
  const { ctx } = useMachine();
  return (
    <Card className="tab-card nav-card" data-testid="care-more">
      <NavList
        items={[
          {
            key: 'settings',
            label: ctx.tr('careSettingsRow'),
            // one line, the descaling drops out first where it does not fit (WRITING.md 0a)
            hint: fitLineOf([
              ctx.num('backflush.remind_after') > 0
                ? ctx.tr('careRemindBf', { n: ctx.num('backflush.remind_after') })
                : null,
              ctx.num('descale.litres') > 0 ? ctx.tr('careRemindDs', { l: ctx.num('descale.litres') }) : null,
            ]),
            onClick: () => onPage({ page: 'settings' }),
            testId: 'care-settings-open',
          },
        ]}
      />
    </Card>
  );
}

/**
 * Als Nächstes (prototype "Wartung"): the drip tray, the backflush and the descaling, each with when it will be due and
 * a soft bar, the most urgent first; the drip tray's button over the whole width once it is almost full, quietly
 * before.
 */
function DueCard() {
  const { ctx } = useMachine();
  const items = dueItems(ctx);
  const drip = dripView(ctx);
  if (!items.length) return null;
  return (
    <Card className="tab-card due-card" data-testid="due-card">
      <CardHeader title={ctx.tr('careNext')} />
      {items.map((it) => (
        <div
          key={it.key}
          className="due-item"
          data-testid={`${it.key === 'backflush' ? 'backflush' : it.key}-card`}
        >
          <Row label={it.name} hint={it.due} testId={`due-${it.key}`}>
            {it.key === 'drip' && !it.now && (
              <ActionButton action={drip.emptied} variant="ghost" testId="drip-emptied-care" />
            )}
          </Row>
          <Bar fraction={it.fraction} due={it.now} label={`${it.name}: ${it.due}`} />
          {it.key === 'drip' && it.now && (
            <ActionButton
              action={drip.emptied}
              variant="primary"
              icon="check"
              block
              testId="drip-emptied-care"
            />
          )}
        </div>
      ))}
    </Card>
  );
}

/** Backflush: one play button, then the choice with or without cleaner (prototype: one row "Backflush"). */
function BackflushStart() {
  const { ctx, send } = useMachine();
  const b = backflushView(ctx);
  const [asking, setAsking] = useState(false);
  const titleId = useId();
  if (!b.start || !b.cleanStart) return null;
  const go = (input: typeof b.start.input) => {
    setAsking(false);
    void send(input);
  };
  return (
    <>
      <button
        type="button"
        className="play"
        aria-label={`${b.title}: ${ctx.tr('bfStart')}`}
        title={`${b.title}: ${ctx.tr('bfStart')}`}
        aria-busy={b.start.busy || undefined}
        disabled={!b.start.enabled}
        onClick={() => setAsking(true)}
        data-testid="bf-start"
      >
        {b.start.busy ? <span className="spinner spinner-sm" aria-hidden="true" /> : <Icon name="play" />}
      </button>
      {asking && (
        <Sheet
          variant="alert"
          title={ctx.tr('bfStartQ')}
          titleId={titleId}
          onClose={() => setAsking(false)}
          testId="bf-start-sheet"
        >
          <p className="text-dim">{ctx.tr('bfConfirm')}</p>
          <div className="sheet-actions">
            <Button block onClick={() => b.start && go(b.start.input)} data-testid="bf-start-confirm">
              {ctx.tr('bfWithout')}
            </Button>
            <Button
              variant="secondary"
              block
              onClick={() => b.cleanStart && go(b.cleanStart.input)}
              data-testid="clean-start-confirm"
            >
              {ctx.tr('bfWith')}
            </Button>
            <Button variant="ghost" block onClick={() => setAsking(false)} data-testid="bf-start-cancel">
              {ctx.tr('cancel')}
            </Button>
          </div>
        </Sheet>
      )}
    </>
  );
}

/**
 * Programme (prototype "Wartung"): flushing and backflush with a round play button each, while one runs its state and
 * the button to stop it; descaling one page deeper (it takes about an hour and has its own steps).
 */
function ProgramsCard({ onPage }: { onPage: (p: CarePage) => void }) {
  const { ctx } = useMachine();
  const f = flushView(ctx);
  const b = backflushView(ctx);
  const d = descaleView(ctx);
  const play = (title: string) => `${title}: ${ctx.tr('bfStart')}`;
  return (
    <Card className="tab-card programs-card rows-card" data-testid="programs-card">
      <CardHeader title={ctx.tr('carePrograms')} />
      <div className="program" data-testid="flush-card">
        <Row label={f.title} hint={ctx.tr('flushRowHint')}>
          {f.start && <ActionButton action={f.start} play={play(f.title)} testId="flush-start" />}
          {f.stop && <ActionButton action={f.stop} variant="danger" testId="flush-stop" />}
        </Row>
        {f.status && (
          <p className="status-line" data-testid="flush-status">
            {f.status}
          </p>
        )}
      </div>
      <div className="program" data-testid="bf-program">
        <Row label={b.title} hint={ctx.tr('bfRowHint', { n: ctx.num('backflush.cycles') || 5 })}>
          <BackflushStart />
          {b.stop && <ActionButton action={b.stop} variant="danger" testId="bf-stop" />}
          {b.cleanStop && <ActionButton action={b.cleanStop} variant="danger" testId="clean-stop" />}
        </Row>
        {b.notes.map((n) => (
          <NoteRow key={n.key} note={n} />
        ))}
      </div>
      <div className="program" data-testid="descale-program">
        <NavList
          items={[
            {
              key: 'descale',
              label: d.title,
              hint: d.status?.text ?? d.needsSensor ?? ctx.tr('descaleRowHint'),
              onClick: () => onPage({ page: 'descale' }),
              testId: 'descale-open',
            },
          ]}
        />
      </div>
    </Card>
  );
}

/**
 * Descaling as a page (board "Entkalken"): the water since the last time against the reminder with its bar and a link
 * to it; the programme in its four steps (the one running marked) with "Programm starten", then "Weiter" and
 * "Stoppen"; descaling without the programme in steps with "Schon entkalkt".
 */
function DescalePage({ onBack, onPage }: { onBack: () => void; onPage: (p: CarePage) => void }) {
  const { ctx } = useMachine();
  const d = descaleView(ctx);
  const w = d.water;
  return (
    <SubScreen tab="wartung" title={d.title} onBack={onBack} testId="descale-page">
      {w && (
        <Card className="tab-card ds-water" data-testid="descale-water">
          <span className="label-dim">{w.label}</span>
          <span className="ds-amount">
            <span className="ds-litres num">{w.text}</span>
            {w.of && <span className="hint">{w.of}</span>}
          </span>
          {w.of && <Bar fraction={w.fraction} due={d.bar?.due ?? false} label={`${w.text} ${w.of}`} />}
          <div className="ds-last">
            <span className="hint">{w.last}</span>
            <button
              type="button"
              className="link-btn link-caps"
              onClick={() => onPage({ page: 'settings' })}
              data-testid="descale-remind"
            >
              {ctx.tr('dsRemindLink')}
              <Icon name="chevronRight" size="sm" />
            </button>
          </div>
        </Card>
      )}
      <Card className="tab-card ds-program" data-testid="descale-card">
        <CardHeader
          title={d.program.title}
          info={{ text: d.program.info, label: ctx.tr('infoShow'), testId: 'descale-info' }}
          action={<span className="hint">{d.program.duration}</span>}
        />
        <ol className="ds-steps">
          {d.program.steps.map((x) => (
            <li
              key={x.n}
              className={x.now ? 'ds-step is-now' : x.done ? 'ds-step is-done' : 'ds-step'}
              aria-current={x.now ? 'step' : undefined}
            >
              <span className="ds-step-n num" aria-hidden="true">
                {x.done ? <Icon name="check" size="sm" /> : x.n}
              </span>
              <span className="row-text">
                <span className="row-label">{x.name}</span>
                <span className="hint">{x.text}</span>
              </span>
            </li>
          ))}
        </ol>
        {d.needsSensor && <p className="hint">{d.needsSensor}</p>}
        {d.status && (
          <Banner tone={d.status.tone === 'warn' ? 'warn' : 'info'} icon="drop" data-testid="descale-status">
            {d.status.text}
          </Banner>
        )}
        {d.start && (
          <ActionButton
            action={{ ...d.start, label: ctx.tr('dsStartProgram') }}
            variant="primary"
            size="md"
            block
            icon="play"
            testId="descale-start"
          />
        )}
        {d.next && <ActionButton action={d.next} variant="primary" size="md" block testId="descale-next" />}
        {d.stop && <ActionButton action={d.stop} variant="danger" size="md" testId="descale-stop" />}
      </Card>
      {/* always open: what to do is the important part, not a detail to unfold (Dominik 22.10.2026) */}
      <Card className="tab-card" data-testid="descale-how">
        <CardHeader title={d.how.title} />
        <ol className="howto">
          {d.how.steps.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ol>
        <p className="hint">{d.how.source}</p>
        <ActionButton action={d.descaled} variant="secondary" size="md" block testId="descaled" />
      </Card>
    </SubScreen>
  );
}

/**
 * The reminders and the backflush programme (board "Erinnerungen"): what it reminds of as the label (Backflush,
 * Entkalken, Tropfschale) with the unit under it, the programme's rounds and times; cards of rows with their (i).
 */
function CareSettingsPage({ onBack }: { onBack: () => void }) {
  const { ctx } = useMachine();
  const remind = [
    controlView(ctx, 'backflush.remind_after', {
      label: 'sBf',
      hint: 'remindShots',
      zeroText: 'zeroNever',
      zeroStart: 50,
    }),
    controlView(ctx, 'descale.litres', {
      label: 'sDescale',
      hint: 'remindWater',
      unit: 'l',
      zeroText: 'zeroNever',
      zeroStart: 40,
    }),
    controlView(ctx, 'drip.capacity', {
      label: 'sDrip',
      hint: 'remindCapacity',
      info: 'dripCapacityInfo',
      unit: 'ml',
      zeroText: 'zeroOff',
      zeroStart: 600,
    }),
  ];
  const program = [
    controlView(ctx, 'backflush.cycles', { label: 'bfCycles' }),
    controlView(ctx, 'backflush.fill_time', { label: 'bfFill', unit: 's' }),
    controlView(ctx, 'backflush.flush_time', { label: 'bfFlush', unit: 's' }),
  ];
  const ids = [
    ['bf-remind', 'descale-litres', 'drip-capacity'],
    ['bf-cycles', 'bf-fill', 'bf-flush'],
  ] as const;
  return (
    <SubScreen tab="wartung" title={ctx.tr('careSettingsRow')} onBack={onBack} testId="care-settings-page">
      <OwnerNote controls={[...remind, ...program]} />
      <Card className="tab-card rows-card" data-testid="care-reminders">
        <CardHeader
          title={ctx.tr('careRemind')}
          info={{ text: ctx.tr('careRemindInfo'), label: ctx.tr('infoShow'), testId: 'remind-info' }}
        />
        {remind.map((c, i) => (
          <ControlRow key={ids[0][i]} control={c} testId={ids[0][i]} />
        ))}
      </Card>
      <Card className="tab-card rows-card" data-testid="care-backflush">
        <CardHeader
          title={ctx.tr('bfProgram')}
          info={{ text: ctx.tr('bfProgramInfo'), label: ctx.tr('infoShow'), testId: 'bf-program-info' }}
        />
        {program.map((c, i) => (
          <ControlRow key={ids[1][i]} control={c} testId={ids[1][i]} />
        ))}
      </Card>
    </SubScreen>
  );
}

/** Zähler (prototype "Wartung"): every shot, the coffee, the water since descaling. */
function CountsCard() {
  const { ctx } = useMachine();
  const facts = counterFacts(ctx);
  if (!facts) return null;
  return (
    <Card className="tab-card" data-testid="count-card">
      <CardHeader title={ctx.tr('sCount')} />
      <FactRow items={facts.map((x) => ({ ...x, testId: `count-${x.key}` }))} />
    </Card>
  );
}
